import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import { redirectForStep } from "../_redux/liveReviewStepRoutes";
import {
  EXECUTION_BLUEPRINT,
  EXECUTION_BLUEPRINT_REVIEW,
  EXECUTION_BLUEPRINT_WIG_CONNECTIONS_LOADING,
} from "../../../../data/RouteUrls";
import liveReviewSocketStore, {
  useLiveReviewSocket,
  LIVE_REVIEW_STEP,
  PARTICIPANT_ROLE,
} from "../ExecutionBlueprintLiveReview/_socket/LiveReviewSocketStore";
import { CheckIcon } from "../../StrategyCanvas/StrategyCanvasHelpers";
import { useExecutionBlueprintStoreSelector } from "../_redux/ExecutionBlueprintStore";
import "./WigConnectionsLoading.scss";

// ─────────────────────────────────────────────────────────────────────────────
// "We're building your WIG Connections" - the wait while the agent refines the
// blueprint and generates the connection map. Figma node 33831:46899.
//
// ⚠️ LIVE as of 2026-08-28, and reachable: this is `live_review_step:
// "connectors_drafting"` (§6a). The Live Review screen sends both parties here
// on the lock broadcast, and this screen listens for the NEXT broadcast to move
// them on to the connector map. Nothing reports PROGRESS for the agent's work —
// that constraint is unchanged and is why the bar is shaped the way it is — but
// completion is now a real signal rather than an absent one.
//
// ── Progress with no progress to report ─────────────────────────────────────
// The design draws a determinate bar at ~58%. Nothing can supply that number,
// so the honest options were: an indeterminate barber-pole (throws away the
// design), a fake bar that hits 100% and then sits there while the work
// continues (actively lies, and is the single most common way a loading screen
// loses trust), or what's implemented here:
//
//   1. The bar eases toward a CEILING it never reaches (92%). It always moves,
//      never completes on its own, and completion only happens when the caller
//      says the work is done. A bar that stalls at 92% reads as "nearly there";
//      one that sits at 100% reads as broken.
//   2. Motion decelerates. Each tick advances a fraction of the REMAINING
//      distance, so it's quick early and slow late - which is also how these
//      jobs actually feel, and it means a long wait never runs out of runway.
//   3. The four steps tick over on a timer, each one landing a check. Discrete
//      milestones make a wait feel shorter than a single bar does, because
//      something visibly finishes every few seconds.
//   4. After 45s the reassurance line changes rather than repeating "less than
//      a minute" at someone who has been waiting longer than that. Nothing
//      erodes trust faster than a screen insisting on a promise it has already
//      broken.
//   5. `prefers-reduced-motion` drops the pulse/draw animations and leaves the
//      state changes, which are the informative part.
//
// `durationMs` only sets the pacing of the step ticks. `done` still completes the
// bar for a caller that passes it, but it is no longer how this screen ends: the
// step change navigates away, so in practice the bar is unmounted mid-ease. That
// is correct — the wait is over because the work is done, not because a bar
// filled up.
// ─────────────────────────────────────────────────────────────────────────────

// ⚠️ THIS SCREEN SERVES ONE DRAFTING STEP: `connectors_drafting`.
// (It served `year1_drafting` too until 2026-08-31. That wait moved onto the
// Year 1 Plan screen as a bare full-screen spinner, on instruction, and the
// separate route was deleted — so the copy switch and the dual "stay here"
// branches went with it. The original note follows.)
// For the record, since it is a contract departure: §6b.2 asks for a Year 1 wait
// "of the same shape as the connectors loading screen". We no longer give it
// one. The instruction was a bare spinner instead, and the shortlist wait is now
// that — see the Year 1 Plan screen. `DRAFT_COPY` keeps its single `connectors`
// key rather than collapsing to a flat object, so restoring a second wait is
// adding a key rather than reshaping the file.
const DRAFT_COPY = {
  connectors: {
    title: "We're building the WIG Connections",
    subtitle:
      "The AI is mapping how the CEO's WIGs contribute to each other and to the BHAG.",
    steps: [
      {
        key: "reading",
        glyph: "\ud83d\udcc4",
        title: "Reading the WIGs",
        text: "Across all four dimensions.",
      },
      {
        key: "analyzing",
        glyph: "\u29c9",
        title: "Finding the links",
        text: "Which WIGs feed others, and which depend on them.",
      },
      {
        key: "building",
        glyph: "\u25cc",
        title: "Drawing the map",
        text: "Laying out every connection, up to the BHAG.",
      },
      {
        key: "finalizing",
        glyph: "\u2611",
        title: "Almost ready",
        text: "You'll review it together on the next screen.",
      },
    ],
    banner: {
      title: "Why connections matter",
      text: "A WIG that feeds many others is a strong candidate for the 12-month priorities.",
    },
  },
};

// Module scope, per convention: the hook resubscribes on selector identity.
const selectStep = (state) => state.snapshot?.live_review_step;

// The steps this screen is the right home for. Everything else routes away
// via the shared table in `_redux/liveReviewStepRoutes.js` —
// `year1_drafting` is NOT ours: the Year 1 screen owns that wait since 2026-08-31.
const OWN_STEPS = [LIVE_REVIEW_STEP.CONNECTORS_DRAFTING];

const selectJoinError = (state) => state.joinError;

const selectClients = (state) => state.clients;

const CEILING = 92;
const TICK_MS = 400;
const SLOW_AFTER_MS = 45000;

// ── Re-join safety net ───────────────────────────────────────────────────────
// This screen advances ONLY when a `live_review_step_changed` broadcast arrives,
// so a broadcast missed for any reason leaves it spinning forever with the work
// already finished. That is a real failure mode the contract names rather than a
// hypothetical: §6a.4 and §6b.3 both say that if you weren't connected when the
// agent finished, "you'll get an email instead of the live update", and both
// prescribe the same remedy — *"Reconnecting and sending `join_live_review`
// again picks up exactly where things are: `live_review_step` is what routes
// your screen, not which broadcast you happened to catch."*
//
// So this re-joins on a slow interval while the wait is on. It is a last resort,
// not a poll of the step: each re-join returns a full `live_review_details`,
// and the existing step-routing effect navigates the moment that snapshot says
// the step has moved.
//
// 20s is chosen to be useless as a poll and adequate as a rescue: the drafting
// agents take far longer than that, so in the normal case the broadcast always
// wins and this never fires more than a couple of times.
const REJOIN_INTERVAL_MS = 20000;

// The node-graph mark above the title: four filled circles joined by three
// lines, plus two sparkles. Rebuilt as one inline SVG at the design's own
// coordinates (node 33831:46900's children) rather than four positioned divs
// and three 1.47px line images - the "assets" in that frame are plain straight
// strokes, so nothing is being redrawn from a glyph here.
//
// The lines draw themselves on mount and the circles breathe out of phase, so
// the mark reads as something being assembled rather than a static logo.
const ConnectionMark = () => (
  <svg
    className="wcl-mark"
    viewBox="0 0 202 138"
    width="202"
    height="138"
    role="img"
    aria-label="Building the connection map"
  >
    <g className="wcl-mark-lines" stroke="#8fd3d6" strokeWidth="2" fill="none">
      <line x1="59" y1="97" x2="100" y2="28" />
      <line x1="112" y1="107" x2="174" y2="55" />
      <line x1="59" y1="97" x2="112" y2="107" />
    </g>
    <g fill="#0d7a82">
      <circle className="wcl-mark-dot" cx="59" cy="92" r="22" />
      <circle className="wcl-mark-dot" cx="115" cy="110" r="14" />
      <circle className="wcl-mark-dot" cx="99" cy="28" r="11" />
      <circle className="wcl-mark-dot" cx="176" cy="56" r="14" />
    </g>
    <g fill="#8fd3d6" fontSize="13" fontWeight="700">
      <text x="129" y="19">
        ✦
      </text>
      <text className="wcl-mark-spark" x="37" y="61" fontSize="9">
        ✦
      </text>
    </g>
  </svg>
);

const StepCard = ({ step, state }) => (
  <div className={`wcl-step wcl-step--${state}`}>
    <div className="wcl-step-icon">
      <span className="wcl-step-glyph" aria-hidden="true">
        {step.glyph}
      </span>
      {state === "done" && (
        <span className="wcl-step-badge">
          <CheckIcon size={10} />
        </span>
      )}
      {/* The ring only spins on the step actually in flight - the design shows
          exactly one dotted ring, on "Building connection map". */}
      {state === "active" && <span className="wcl-step-ring" />}
    </div>
    <p className="wcl-step-title">{step.title}</p>
    <p className="wcl-step-text">{step.text}</p>
  </div>
);

const WigConnectionsLoading = ({
  // Flip to true when the real work finishes; the bar completes and every step
  // lands a check. Left false here because nothing signals it yet.
  done = false,
  durationMs = 40000,
  onComplete,
}) => {
  const history = useHistory();
  const { blueprintId } = useParams();
  const [percent, setPercent] = useState(6);
  const [stepIndex, setStepIndex] = useState(0);
  const [slow, setSlow] = useState(false);
  const startedAt = useRef(Date.now());

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = `Art of Scale | ${copy.title.replace("We\u2019re ", "").replace("We're ", "")}`;
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Execution Blueprint" });
    // eslint-disable-next-line
  }, []);

  const dispatch = useDispatch();
  const clients = useExecutionBlueprintStoreSelector(selectClients);
  const companyName =
    (clients || []).find((c) => c.blueprintId === blueprintId)?.name || "";

  // ⚠️ THIS SCREEN MUST JOIN THE SOCKET ITSELF, and that is not an optimisation.
  //
  // The Live Review screen calls `disconnect()` when it unmounts, which closes the
  // transport and resets the store — deliberately, so whoever leaves stops being
  // reported as present. So by the time this screen mounts there is NO live
  // connection and nobody is listening for anything.
  //
  // §6a sends `live_review_step_changed` TWICE: `connectors_drafting` (which is
  // what routed the user here) and then `connectors_review` with the graph, "no
  // fixed timing". Without a connection of its own this screen would never hear
  // the second one, and both parties would sit watching the building animation
  // forever with the work already finished. That is exactly the bug this comment
  // exists to stop anyone reintroducing by "tidying" the connect away.
  //
  // §6a.4 is the same requirement from the other direction: someone who was away
  // when the agent finished gets an email rather than a broadcast, and lands here
  // cold. Re-joining is what tells them where things actually are.
  const stepFromStore = useLiveReviewSocket(selectStep);
  const joinError = useLiveReviewSocket(selectJoinError);
  const step = stepFromStore;

  // Which wait this is. Defaults to the connectors copy so a mount before the
  // snapshot lands shows the earlier stage rather than a blank screen — and the
  // step corrects it within a tick of joining.
  // Connectors only. The Year 1 wait used to share this screen; it now lives
  // on the Year 1 Plan screen as a bare spinner (2026-08-31, on instruction),
  // so there is one wait here and no step to choose between.
  const copy = DRAFT_COPY.connectors;
  const STEPS = copy.steps;

  // ── "Execution Blueprint › ‹Company› WIG Connections" ─────────────────────
  // Exactly TWO crumbs, on instruction. The company and the stage are ONE label
  // — "Acme Ltd WIG Connections" — because they name a single thing: this
  // client's stage. Split into separate crumbs they read as a hierarchy with
  // levels you could climb to, and those levels don't exist: the async review is
  // off-limits once the live review has started, and the live review screen
  // bounces anyone whose step has moved on.
  //
  // ⚠️ THE STAGE NAME IS COMPUTED, because this one component serves BOTH
  // drafting waits (see the header) on two different routes. The step says which
  // wait this is, so the trail reads "Year 1 Plan" while the shortlist is being
  // drafted and "WIG Connections" while the connector map is.
  //
  // ⚠️ It dispatches under BOTH route keys deliberately. `ProjectHeader` looks
  // the trail up by the matched route, so whichever of the two routes brought us
  // here has to find it — only one ever will, so writing both is harmless, and
  // the alternative is a screen that silently falls back to its static trail on
  // the Year 1 route.
  //
  // ⚠️ Declared HERE, below `copy`/`stepFromStore`, not up with the other
  // effects. An earlier version of this file put a step-reading effect above
  // those consts and crashed on the temporal dead zone.
  //
  // Only the first crumb navigates (the coach's client dashboard).
  useEffect(() => {
    const stage =
      stepFromStore === LIVE_REVIEW_STEP.YEAR1_DRAFTING
        ? "Prioritised WIGs for the next 12 months"
        : "WIG Connections";
    const crumbs = [
      breadCrumbsArray[EXECUTION_BLUEPRINT][0],
      { title: companyName ? `${companyName} ${stage}` : stage, url: "" },
    ];
    dispatch(
      setDynamicBreadcrumbs(
        EXECUTION_BLUEPRINT_WIG_CONNECTIONS_LOADING,
        crumbs,
      ),
    );
  }, [dispatch, companyName, blueprintId, stepFromStore]);

  useEffect(() => {
    liveReviewSocketStore
      .getState()
      .connect({ uniqueId: blueprintId, viewerRole: PARTICIPANT_ROLE.COACH });
    return () => liveReviewSocketStore.getState().disconnect();
    // eslint-disable-next-line
  }, [blueprintId]);

  // The step routes the screen, in every direction. `replace` so the browser's
  // Back button doesn't drop the user onto a wait that is already over.
  useEffect(() => {
    // Only `connectors_drafting` belongs here. `year1_drafting` used to be a
    // "stay put" step for this screen too; the Year 1 screen owns that wait
    // since 2026-08-31, so it routes away like any other step.
    //
    // ⚠️ THIS FIXES A REAL BUG. The ladder this replaces ended in a bare `else`
    // that sent everything unmatched to the Live Review screen — including
    // `year1_drafting`, which is reachable here because this component is still
    // registered on the `YEAR_ONE_PLAN_DRAFTING` route. Anyone landing there
    // during the Year 1 wait was bounced backwards into the finalize screen.
    const target = redirectForStep(step, OWN_STEPS, blueprintId);
    if (target) history.replace(target);
  }, [step, blueprintId, history]);

  // The rescue described at REJOIN_INTERVAL_MS. Runs only while the step still
  // says "drafting" (or hasn't arrived), and stops the moment it moves — at
  // which point the effect above has already navigated away.
  useEffect(() => {
    if (!blueprintId) return undefined;
    const waiting = !step || step === LIVE_REVIEW_STEP.CONNECTORS_DRAFTING;
    if (!waiting) return undefined;

    const timer = setInterval(() => {
      liveReviewSocketStore.getState().join();
    }, REJOIN_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [blueprintId, step]);

  // Eases toward CEILING by a fixed fraction of what's left each tick, so it
  // decelerates on its own and can never arrive.
  useEffect(() => {
    if (done) return undefined;
    const timer = setInterval(() => {
      setPercent((prev) => prev + (CEILING - prev) * 0.06);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [done]);

  // Steps tick over on an even split of `durationMs`, stopping on the last one
  // rather than marking it done - the final step only completes for real.
  useEffect(() => {
    if (done) return undefined;
    const per = Math.max(durationMs / STEPS.length, 1200);
    const timer = setInterval(() => {
      setStepIndex((prev) => Math.min(prev + 1, STEPS.length - 1));
    }, per);
    return () => clearInterval(timer);
  }, [done, durationMs, STEPS.length]);

  useEffect(() => {
    if (done) return undefined;
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [done]);

  useEffect(() => {
    if (!done) return;
    setPercent(100);
    setStepIndex(STEPS.length);
    if (onComplete) onComplete();
  }, [done, onComplete, STEPS.length]);

  const stepStates = useMemo(
    () =>
      STEPS.map((step, index) => {
        if (index < stepIndex) return "done";
        if (index === stepIndex) return "active";
        return "pending";
      }),
    [stepIndex, STEPS],
  );

  const elapsed = Math.round((Date.now() - startedAt.current) / 1000);

  return (
    <div>
      <ProjectHeader
        handleNavigation={() =>
          history.push(
            EXECUTION_BLUEPRINT_REVIEW.replace(":blueprintId", blueprintId),
          )
        }
        showBack
      />
      <div id="wig-connections-loading">
        <div className="wcl-inner">
          <ConnectionMark />

          <h1 className="wcl-title">{copy.title}</h1>
          <p className="wcl-subtitle">{copy.subtitle}</p>

          {/* aria-valuenow is omitted while indeterminate: announcing a number
              this screen made up would be worse than announcing none. */}
          <div
            className="wcl-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={done ? 100 : undefined}
            aria-label="Building the connection map"
          >
            <div className="wcl-progress-track">
              <div
                className={`wcl-progress-fill ${done ? "is-done" : ""}`}
                style={{ width: `${Math.min(percent, 100)}%` }}
              />
            </div>
          </div>

          <div className="wcl-meta" aria-live="polite">
            {/* A failed join outranks both the normal and the "taking a while"
                copy. Without this branch a socket that never joined looks
                identical to an agent that is still working — the bar keeps
                easing and the steps keep ticking, because both are on timers —
                so the one state the user can actually act on is the one they
                could not see. */}
            {joinError ? (
              <>
                <span className="wcl-meta-line">
                  We can&apos;t reach the live session right now.
                </span>
                <span className="wcl-meta-sub">
                  Your work is safe and nothing has been lost. This page keeps
                  retrying on its own — if it doesn&apos;t recover, reload.
                </span>
              </>
            ) : slow && !done ? (
              <>
                <span className="wcl-meta-line">
                  Still working — this one&apos;s taking a little longer than
                  usual.
                </span>
                <span className="wcl-meta-sub">
                  Nothing&apos;s wrong, and nothing will be lost. It will appear here as soon as it&apos;s ready
                  {elapsed ? ` (${elapsed}s so far)` : ""}.
                </span>
              </>
            ) : (
              <>
                <span className="wcl-meta-line">
                  <span className="wcl-meta-clock" aria-hidden="true">
                    🕐
                  </span>
                  This usually takes less than a minute.
                </span>
                <span className="wcl-meta-sub">
                  Your work is saved as you go.
                </span>
              </>
            )}
          </div>

          <div className="wcl-steps">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.key}>
                {index > 0 && (
                  <div className="wcl-step-connector" aria-hidden="true">
                    <span
                      className={`wcl-step-connector-line ${
                        stepStates[index] !== "pending" ? "is-filled" : ""
                      }`}
                    />
                  </div>
                )}
                <StepCard step={step} state={stepStates[index]} />
              </React.Fragment>
            ))}
          </div>

          <div className="wcl-banner">
            <span className="wcl-banner-spark" aria-hidden="true">
              ✦
            </span>
            <div>
              <p className="wcl-banner-title">{copy.banner.title}</p>
              <p className="wcl-banner-text">{copy.banner.text}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WigConnectionsLoading;
